import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Input, InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { useCacheDomainCredentials } from "@/hooks/useCacheDomainCredentials";
import { useCheckVPNConnectivity } from "@/hooks/useCheckVPNConnectivity";

import { ActionType, AppContext } from "../../stores";
import { STEP_ID } from "../constants";

/**
 * A step that caches the new hire's ANT domain credentials on their Windows
 * laptop so they can sign in with their Amazon password without first
 * connecting to VPN after a restart. This is the Windows parallel to the Mac
 * Pre-Cache AD step and only renders when the Electron credential-caching hook
 * is available (see getEnabledSteps).
 */
export default function AmazonAccountCaching(): JSX.Element {
  const intl = useIntl();
  const { state, dispatch } = useContext(AppContext);
  const { amazonUsername } = state;
  const [password, setPassword] = useState<string>("");
  const { cacheCredentials, isLoading, isSuccess, error } =
    useCacheDomainCredentials();

  // Drive a VPN connectivity check while on this step. Caching domain
  // credentials requires reaching a domain controller, which requires VPN.
  const vpnQuery = useCheckVPNConnectivity();
  const isVpnConnected =
    state.isNetworkConnected || vpnQuery.data === true || vpnQuery.isLoading;

  const regularFontWeight = "regular";

  /**
   * Set the onboarding step and the refPage to provide context to IT Support
   * should the new hire initiate a chat with support. The onboarding step value
   * is shared with the Mac Pre-Cache AD step since it represents the same
   * concept (account caching).
   */
  useEffect(() => {
    updateOnboardingStep("D1_PRECACHE_AD");
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-amazon-account-cache",
    });
  }, []);

  /**
   * Cache the new hire's domain credentials. On both success and failure we
   * enable the Next button so the new hire is never blocked from continuing;
   * on failure we surface an error encouraging them to try again. Success and
   * failure are recorded as authoritative Katal metrics in the Electron main
   * process (it knows the true PowerShell outcome), so we don't emit duplicate
   * client-side metrics here; the step also reports Continue/SectionToggled via
   * its configured eventCategory.
   */
  const handleCache = async (): Promise<void> => {
    const succeeded = await cacheCredentials(amazonUsername ?? "", password);

    // Allow the new hire to continue regardless of the outcome.
    dispatch({
      stepId: STEP_ID.AMAZON_ACCOUNT_CACHING,
      type: ActionType.SET_NEXT_BTN_STATUS,
      enableNext: true,
    });

    if (succeeded) {
      // Don't keep the password around once it has been used.
      setPassword("");
    }
  };

  const isCacheDisabled = !isVpnConnected || isLoading || password.length === 0;

  return (
    <Col gridGap="S400">
      <StepBodyStyledText>
        <Text
          fontWeight={regularFontWeight}
          dataTestId="amazonAccountCachingIntro"
        >
          <FormattedMessage id="amazonAccountCaching-expander-body-intro" />
        </Text>
      </StepBodyStyledText>

      {!isVpnConnected && (
        <MessageBanner
          dataTestId="amazonAccountCachingVpnWarning"
          type={MessageBannerType.Warning}
        >
          <FormattedMessage id="amazonAccountCaching-vpn-warning" />
        </MessageBanner>
      )}

      <View maxWidth="400px" width="100%">
        <InputWrapper
          id="amazonAccountCachingPasswordInput"
          labelText={intl.formatMessage({
            id: "amazonAccountCaching-password-label",
          })}
          dataTestId="amazonAccountCachingPasswordWrapper"
        >
          {({ id, ...inputProps }) => (
            <Input
              {...inputProps}
              id={id}
              type="password"
              value={password}
              onChange={(evt) => setPassword(evt.target.value)}
              error={!!error}
              disabled={isLoading}
              dataTestId="amazonAccountCachingPasswordInput"
            />
          )}
        </InputWrapper>
      </View>

      {isSuccess && (
        <MessageBanner
          dataTestId="amazonAccountCachingSuccess"
          type={MessageBannerType.Success}
        >
          <FormattedMessage id="amazonAccountCaching-success" />
        </MessageBanner>
      )}

      {error && (
        <MessageBanner
          dataTestId="amazonAccountCachingError"
          type={MessageBannerType.Error}
        >
          <FormattedMessage id="amazonAccountCaching-error" />
        </MessageBanner>
      )}

      <Row>
        <Button
          variant={ButtonVariant.Primary}
          dataTestId="amazonAccountCachingCacheBtn"
          disabled={isCacheDisabled}
          onClick={() => void handleCache()}
        >
          <Text fontWeight="bold">
            <FormattedMessage
              id={
                isLoading
                  ? "amazonAccountCaching-button-caching"
                  : "amazonAccountCaching-button-cache"
              }
            />
          </Text>
        </Button>
      </Row>
    </Col>
  );
}
